/** Toolbar control for a grid's count mode.
 *
 *  One icon button → a three-item radio menu (Instant / In background / On
 *  demand), each with a one-line note on what it costs. Rendered only when the
 *  page passes `countMode` to `GridControlsToolbar`, so the grids that stay on
 *  instant totals show no new control.
 *
 *  The choice persists per grid per user (`grid_count_mode:<grid>`), not in a
 *  grid preset: a preset is a shareable *view*, and a performance preference
 *  should not travel to a colleague or silently change what their grid costs.
 */
import CheckIcon from '@mui/icons-material/Check';
import NumbersIcon from '@mui/icons-material/Numbers';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';

import { HelpLink } from '@/components/HelpLink';

import { COUNT_MODE_LABELS, type CountMode } from './useCountMode';

const ORDER: CountMode[] = ['sync', 'async', 'deferred'];

export interface CountModeToolbarState {
  mode: CountMode;
  setMode: (mode: CountMode) => void;
}

export function CountModeMenu({ mode, setMode }: CountModeToolbarState) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);
  const pick = (next: CountMode) => {
    setMode(next);
    close();
  };

  return (
    <>
      <Tooltip title={`Row total: ${COUNT_MODE_LABELS[mode].label.toLowerCase()}`}>
        <IconButton size="small" onClick={open} aria-label="Row total settings">
          <NumbersIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <Box sx={{ px: 2, pt: 0.5, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            Row total
          </Typography>
          <HelpLink story="ui-count-modes" />
        </Box>
        {ORDER.map((option) => (
          <MenuItem key={option} selected={option === mode} onClick={() => pick(option)}>
            <ListItemIcon>{option === mode ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText
              primary={COUNT_MODE_LABELS[option].label}
              secondary={COUNT_MODE_LABELS[option].hint}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
